import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Configuración de i18next basada en ejemplos funcionales
i18n
  .use(initReactI18next) // Integración con React
  .use(LanguageDetector) // Detectar idioma del navegador
  .use(HttpBackend) // Cargar traducciones desde archivos JSON
  .init({
    // Namespaces disponibles.
    //
    // 🔴 TIENE QUE ESTAR AQUÍ TODO FICHERO DE `public/locales/<lng>/`.
    //
    // Con `i18next-http-backend` un namespace SOLO se descarga si está en esta
    // lista o si algún componente montado lo pide en su `useTranslation`. Si no
    // se cumple ninguna de las dos, `t('management:teamsList')` no lanza, no
    // avisa y no cae a ningún texto por defecto: pinta LA CLAVE EN CRUDO.
    //
    // Y engaña, porque en cuanto una pantalla cualquiera carga el namespace,
    // éste se queda en memoria para el resto de la sesión: el mismo literal se
    // ve bien o mal según por dónde hayas pasado antes.
    //
    // `management` y `budgets` faltaban aquí: existían en disco desde siempre,
    // y mientras cada uso vivía dentro de su propia pantalla (que sí los pedía)
    // no se notó. Se destapó al usar `management:teamsList` en el menú de
    // usuario, que va en el Header y por tanto se pinta en TODAS las páginas,
    // incluidas las que no cargan `management`.
    //
    // 🔴 Declararlos aquí NO ES OPCIONAL: pedirlos sólo desde el
    // `useTranslation` del componente es PEOR QUE EL PROPIO BUG. Medido en
    // navegador el 2026-09-07 contra beta, con `management` fuera de esta lista
    // pero presente en el hook del menú de usuario: al abrirlo, React lanza
    //
    //   «A component suspended while responding to synchronous input»
    //   The above error occurred in the <DropDownUserMenu> component
    //
    // y la aplicación ENTERA cae a la pantalla «Se ha producido un error». Es
    // que `useSuspense` está activo por defecto y no hay ningún `<Suspense>`
    // que recoja la suspensión, así que en vez de un literal feo te llevas la
    // pantalla muerta. Al estar en `ns` el namespace se carga en el init, antes
    // de que nadie pinte, y no hay nada que suspender.
    //
    // El coste son ~3 KB, y el service worker ya los precachea con el glob
    // `locales/**/*.json` de vite.config.js: sin cobertura tampoco cambia nada.
    // Hay un tripwire que vigila que esta lista no se quede corta:
    // tests/unit/i18n/declared-namespaces.spec.ts
    ns: [
      'common',
      'auth',
      'navigation',
      'forms',
      'pages',
      'errors',
      'tables',
      'buttons',
      'modals',
      'toasts',
      'validation',
      'campaigns',
      'users',
      'workOrders',
      'communications',
      'accidents',
      'multimedia',
      'management',
      'budgets',
    ],

    // Namespace por defecto
    defaultNS: 'common',

    // Idioma de respaldo (cuando la detección falla)
    fallbackLng: 'es',

    // Idiomas soportados
    supportedLngs: ['ca', 'es', 'en'],

    // Configuración de detección de idioma
    detection: {
      // 🔴 EL ORDEN IMPORTA, Y NO ES EL INTUITIVO.
      //
      // i18next recorre `order` y se queda con el PRIMER idioma soportado que
      // encuentre. Con `navigator` delante, `navigator.language` (es-ES → es
      // vía `convertDetectedLanguage`) siempre está en `supportedLngs`, así que
      // gana siempre y `localStorage` no llega a consultarse NUNCA: el usuario
      // elegía idioma, se guardaba bien, y en cada recarga volvía al del
      // dispositivo. Con el dispositivo en un idioma no soportado tampoco se
      // salvaba, porque el `htmlTag` (`<html lang="es">` en index.html) también
      // iba por delante.
      //
      // La elección explícita del usuario manda sobre la del dispositivo: si
      // nunca eligió, localStorage/cookie no devuelven nada y se cae al
      // navegador, que es el comportamiento que se quería de origen.
      // Mismo orden que rois-front-v2 (`src/i18n/index.ts`).
      order: ['localStorage', 'cookie', 'navigator', 'htmlTag'],
      caches: ['localStorage', 'cookie'],
      lookupLocalStorage: 'rois-language',
      lookupCookie: 'rois-language',
      // Convertir códigos de idioma (en-US -> en, es-ES -> es)
      convertDetectedLanguage: (lng) => lng.split('-')[0],
    },

    // Configuración del backend (carga de archivos)
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      // Forzar recarga de recursos cuando cambia el idioma
      allowMultiLoading: false,
      crossDomain: false,
    },

    // Configuración de interpolación
    interpolation: {
      escapeValue: false, // React ya escapa por defecto
    },

    // Debug en desarrollo
    //debug: import.meta.env.DEV,
    debug: false,

    // Configuración de carga
    load: 'languageOnly', // Solo cargar idioma, no región (es en lugar de es-ES)

    // Configuración de limpieza de códigos de idioma
    cleanCode: true, // Limpiar códigos de idioma (es-ES -> es)

    // Configuración de idiomas no explícitos
    nonExplicitSupportedLngs: true,
  });

// El `<html lang>` de index.html está fijo en "es". Al arrancar, i18next ya
// resuelve bien el idioma (localStorage → cookie → navegador), pero nadie
// actualizaba el atributo: sólo se tocaba dentro de `changeLanguage`
// (useLanguage.ts), que es el camino de "el usuario elige idioma a mano".
// Resultado: tras CADA recarga el documento seguía diciendo `lang="es"`
// aunque la interfaz estuviera en catalán o en inglés.
//
// Importa porque ese atributo no es decorativo: los lectores de pantalla
// pronuncian según él, el navegador ofrece "traducir esta página" cuando no
// coincide con el idioma real, y de él dependen `:lang()` y la partición de
// palabras. Enganchándolo al evento se cubren los dos casos —el arranque y
// los cambios posteriores— en un solo sitio.
// El guard de `document` NO es defensivo por si acaso: este módulo se importa
// también desde Node, sin DOM — lo hace el helper de i18n de los tests E2E
// (`tests/e2e/helpers/i18n.helper.ts`) para leer las traducciones. Sin el
// guard, el import reventaba con "document is not defined" y se llevaba por
// delante la ejecución del módulo entero de tests.
const syncHtmlLang = (lng: string) => {
  if (!lng || typeof document === 'undefined') return;
  document.documentElement.lang = lng;
};

i18n.on('languageChanged', syncHtmlLang);
syncHtmlLang(i18n.resolvedLanguage ?? i18n.language);

export default i18n;
